
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTopButton from "../components/BackToTopButton";
import FloatingSocialButtons from "../components/FloatingSocialButtons";
import { Video, Film, Play, Edit, Camera, Mic, Lightbulb, Award } from "lucide-react";

const VideoAdsPage = () => {
  useEffect(() => {
    document.title = "Video Ads & Production - DG IT Pvt. Ltd.";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <Video className="w-8 h-8 text-purple-600" />,
      title: "Commercial Video Ads",
      description: "High-impact commercial videos that showcase your brand and drive conversions across all platforms"
    },
    {
      icon: <Film className="w-8 h-8 text-purple-600" />,
      title: "Product Demonstrations",
      description: "Engaging product videos that highlight features, benefits, and use cases effectively"
    },
    {
      icon: <Play className="w-8 h-8 text-purple-600" />,
      title: "Social Media Videos",
      description: "Platform-optimized videos for Facebook, Instagram, TikTok, YouTube, and LinkedIn"
    },
    {
      icon: <Edit className="w-8 h-8 text-purple-600" />,
      title: "Video Editing & Post-Production",
      description: "Professional editing with color correction, motion graphics, effects, and sound design"
    },
    {
      icon: <Camera className="w-8 h-8 text-purple-600" />,
      title: "Live Action Production",
      description: "Full-service video production with professional equipment and experienced crew"
    },
    {
      icon: <Mic className="w-8 h-8 text-purple-600" />,
      title: "Voice Over & Sound Design",
      description: "Professional voice overs, music selection, and custom sound design for your videos"
    },
    {
      icon: <Lightbulb className="w-8 h-8 text-purple-600" />,
      title: "Creative Concept Development",
      description: "Innovative storytelling and creative concepts that resonate with your audience"
    },
    {
      icon: <Award className="w-8 h-8 text-purple-600" />,
      title: "Brand Video Production",
      description: "Corporate videos, testimonials, and brand storytelling that build trust and credibility"
    }
  ];

  const videoTypes = [
    { name: "Explainer Videos", icon: "📚" },
    { name: "Product Demos", icon: "🎯" },
    { name: "Brand Stories", icon: "📖" },
    { name: "Social Media Ads", icon: "📱" },
    { name: "YouTube Ads", icon: "📺" },
    { name: "Testimonials", icon: "⭐" },
    { name: "Training Videos", icon: "🎓" },
    { name: "Event Coverage", icon: "🎪" }
  ];

  const features = [
    "4K Ultra HD Quality",
    "Drone Footage Available",
    "Animation & Motion Graphics",
    "Multi-language Support",
    "Fast Turnaround Time",
    "Unlimited Revisions",
    "Social Media Optimization",
    "Cross-platform Compatibility"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <meta name="description" content="Professional video production and advertising services in Nepal. Commercial videos, product demos, social media content, and video marketing." />
        <meta name="keywords" content="video production, video ads, commercial videos, video editing, video marketing, Nepal" />
        <meta property="og:title" content="Video Ads & Production - DG IT Pvt. Ltd." />
      </Helmet>
      
      <Navbar />
      <main className="flex-grow pt-24">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-purple-800 mb-4">
              Video Ads & Production
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Create compelling video content that captivates your audience and drives engagement across all platforms.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card rounded-xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Video Types Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-purple-50 rounded-2xl p-8 mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Types of Videos We Create</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {videoTypes.map((type, index) => (
                <div key={index} className="bg-white rounded-lg p-4 text-center shadow-md hover:shadow-lg transition-shadow">
                  <div className="text-3xl mb-2">{type.icon}</div>
                  <h3 className="font-semibold">{type.name}</h3>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Features Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">What We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <span className="font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Production Process */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-12">Our Production Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {[
                { step: "01", title: "Concept", desc: "Brainstorming and script development" },
                { step: "02", title: "Planning", desc: "Storyboarding and production planning" },
                { step: "03", title: "Filming", desc: "Professional video production" },
                { step: "04", title: "Editing", desc: "Post-production and editing" },
                { step: "05", title: "Delivery", desc: "Final video delivery and optimization" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Stats Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-gray-50 rounded-2xl p-8 mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Video Marketing Impact</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-purple-600">86%</div>
                <div className="text-gray-600">Businesses Use Video</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600">200+</div>
                <div className="text-gray-600">Videos Produced</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600">400%</div>
                <div className="text-gray-600">Engagement Increase</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600">95%</div>
                <div className="text-gray-600">Message Retention</div>
              </div>
            </div>
          </motion.div>

          {/* Equipment Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Professional Equipment</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-purple-50 rounded-xl">
                <Camera className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">4K Cameras</h3>
                <p className="text-gray-600">Professional cinema-grade cameras for stunning visuals</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-xl">
                <Mic className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Audio Equipment</h3>
                <p className="text-gray-600">Crystal clear audio recording with professional microphones</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-xl">
                <Lightbulb className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Lighting Setup</h3>
                <p className="text-gray-600">Professional lighting equipment for perfect illumination</p>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Create Amazing Videos?</h2>
            <p className="text-xl text-purple-100 mb-6">
              Let's bring your vision to life with professional video production services that deliver results.
            </p>
            <motion.a
              href="/quote"
              whileHover={{ scale: 1.05 }}
              className="inline-block px-8 py-4 bg-white text-purple-600 font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Get Started Now
            </motion.a>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTopButton />
      <FloatingSocialButtons />
    </div>
  );
};

export default VideoAdsPage;
